<template>
  <div class="ui-tooltip-wrapper" ref="wrapper">
    <slot name="trigger"></slot>
    <!-- fixed: transform 있는 모달( scale-in 등 ) 안에서 viewport 좌표와 fixed 기준이 어긋나지 않도록 body로 분리 -->
    <Teleport to="body" :disabled="!fixed">
    <div
      ref="tooltip"
      class="ui-tooltip"
      :class="[
        `ui-tooltip--${currentPosition}`,
        tooltipClass,
        {
          'ui-tooltip--show': isVisible,
          'ui-tooltip--fixed': fixed,
          'ui-tooltip--kpi-gray': variant === 'kpi-gray',
          'ui-tooltip--activity-user-profile': variant === 'activity-user-profile',
        }
      ]"
      :style="fixed ? fixedStyle : {}"
    >
      <div class="ui-tooltip-content" :class="{ 'ui-tooltip-content--scrollable-title': titleMaxLines != null }" :style="scrollableTitleStyle">
        <slot name="title">
          <strong v-if="title">{{ title }}</strong>
        </slot>
        <slot></slot>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<script>
  export default {
    name: 'UiTooltip',
    props: {
      /**
       * 툴팁 제목
       */
      title: {
        type: String,
        default: ''
      },
      /**
       * 툴팁 위치 (top, right, right-start, bottom, left, bottom-left, bottom-right, top-left, top-right)
       */
      position: {
        type: String,
        default: 'bottom',
        validator: value =>
          ['top', 'right', 'right-start', 'bottom', 'left', 'bottom-left', 'bottom-right', 'top-left', 'top-right'].includes(value)
      },
      /**
       * 툴팁 표시 방식 (hover, click)
       */
      trigger: {
        type: String,
        default: 'hover',
        validator: value => ['hover', 'click'].includes(value)
      },
      /**
       * position: fixed 사용 여부 (overflow 문제 해결용)
       */
      fixed: {
        type: Boolean,
        default: false
      },
      /**
       * title(본문)이 이 줄 수보다 길 때 영역 높이를 제한하고 세로 스크롤 표시 (null 이면 제한 없음)
       */
      titleMaxLines: {
        type: Number,
        default: null,
        validator: value => value == null || (Number.isFinite(value) && value > 0)
      },
      /**
       * fixed 모드에서 뷰포트 여백이 부족할 때 top↔bottom 자동 전환
       */
      autoFlip: {
        type: Boolean,
        default: false
      },
      /**
       * kpi-gray: KPI 게이지 등 회색 툴팁 (fixed+Teleport 시에도 variant 클래스로 스타일 유지)
       */
      variant: {
        type: String,
        default: 'default',
        validator: value => ['default', 'kpi-gray', 'activity-user-profile'].includes(value)
      },
      /**
       * Teleport(body)로 분리되는 .ui-tooltip에 추가할 클래스
       * (fixed 모드에서는 부모 scoped/:deep()이 닿지 않으므로 화면별 스타일 타겟용)
       */
      tooltipClass: {
        type: [String, Array, Object],
        default: ''
      },
      /**
       * fixed 모드 z-index (미지정 시 부모 오버레이 기준 자동 보정, 기본 10050)
       */
      zIndex: {
        type: Number,
        default: null
      },
      /**
       * 트리거가 실제로 말줄임(...)된 경우에만 툴팁을 띄운다.
       * 그리드 셀처럼 짧은 값은 이미 다 보이는데도 툴팁이 떠서 가리는 걸 막는 용도.
       * 기본 false 라 기존 사용처 동작은 그대로다.
       */
      onlyWhenTruncated: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        isVisible: false,
        fixedStyle: {},
        hoverLeaveTimer: null,
        // autoFlip 적용 후 실제 사용 위치 (null이면 prop의 position 그대로 사용)
        effectivePosition: null
      }
    },
    computed: {
      scrollableTitleStyle() {
        if (this.titleMaxLines == null) return {}
        return { '--tooltip-title-max-lines': String(this.titleMaxLines) }
      },
      currentPosition() {
        return this.effectivePosition || this.position
      }
    },
    mounted() {
      this.$nextTick(() => {
        this.bindTooltipEvents()
      })
    },
    beforeDestroy() {
      this.clearHoverLeaveTimer()
      this.unbindTooltipEvents()
    },
    methods: {
      getTriggerEl() {
        const wrapper = this.$refs.wrapper
        return wrapper?.firstElementChild ?? null
      },
      getTooltipEl() {
        return this.$refs.tooltip ?? null
      },
      bindTooltipEvents() {
        const triggerEl = this.getTriggerEl()
        if (!triggerEl) return

        if (this.trigger === 'hover') {
          triggerEl.addEventListener('mouseenter', this.onHoverTriggerEnter)
          triggerEl.addEventListener('mouseleave', this.onHoverTriggerLeave)
          const tooltipEl = this.getTooltipEl()
          if (tooltipEl) {
            tooltipEl.addEventListener('mouseenter', this.onHoverTooltipEnter)
            tooltipEl.addEventListener('mouseleave', this.onHoverTooltipLeave)
          }
        } else if (this.trigger === 'click') {
          triggerEl.addEventListener('click', this.toggleTooltip)
          document.addEventListener('click', this.handleOutsideClick)
        }
      },
      unbindTooltipEvents() {
        const triggerEl = this.getTriggerEl()

        if (this.trigger === 'hover') {
          if (triggerEl) {
            triggerEl.removeEventListener('mouseenter', this.onHoverTriggerEnter)
            triggerEl.removeEventListener('mouseleave', this.onHoverTriggerLeave)
          }
          const tooltipEl = this.getTooltipEl()
          if (tooltipEl) {
            tooltipEl.removeEventListener('mouseenter', this.onHoverTooltipEnter)
            tooltipEl.removeEventListener('mouseleave', this.onHoverTooltipLeave)
          }
        } else if (this.trigger === 'click') {
          if (triggerEl) {
            triggerEl.removeEventListener('click', this.toggleTooltip)
          }
          document.removeEventListener('click', this.handleOutsideClick)
        }
      },
      /**
       * 툴팁 표시
       */
      /** 트리거가 가로/세로로 잘려 있는지 (onlyWhenTruncated 판정용) */
      isTriggerTruncated() {
        const el = this.getTriggerEl()
        if (!el) return false
        return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1
      },
      showTooltip() {
        if (this.onlyWhenTruncated && !this.isTriggerTruncated()) return
        if (this.fixed) {
          this.calculateFixedPosition()
        }
        this.isVisible = true
      },
      clearHoverLeaveTimer() {
        if (this.hoverLeaveTimer != null) {
          clearTimeout(this.hoverLeaveTimer)
          this.hoverLeaveTimer = null
        }
      },
      /**
       * 트리거↔툴팁 사이 간격을 지나갈 때 잠깐 닫혀 버리지 않도록 지연 후 닫기
       */
      scheduleHideTooltip() {
        this.clearHoverLeaveTimer()
        this.hoverLeaveTimer = setTimeout(() => {
          this.hoverLeaveTimer = null
          this.hideTooltip()
        }, 72)
      },
      onHoverTriggerEnter() {
        this.clearHoverLeaveTimer()
        this.showTooltip()
      },
      onHoverTriggerLeave() {
        this.scheduleHideTooltip()
      },
      onHoverTooltipEnter() {
        this.clearHoverLeaveTimer()
      },
      onHoverTooltipLeave() {
        this.scheduleHideTooltip()
      },
      /**
       * 툴팁 숨기기
       */
      hideTooltip() {
        this.clearHoverLeaveTimer()
        this.isVisible = false
      },
      /**
       * 툴팁 토글
       */
      toggleTooltip() {
        if (!this.isVisible && this.fixed) {
          this.calculateFixedPosition()
        }
        this.isVisible = !this.isVisible
      },
      /**
       * 외부 클릭 처리
       */
      handleOutsideClick(event) {
        const wrapper = this.$refs.wrapper
        const tooltipEl = this.getTooltipEl()
        const inWrapper = wrapper?.contains(event.target)
        const inTooltip = tooltipEl?.contains(event.target)
        if (!inWrapper && !inTooltip && this.isVisible) {
          this.hideTooltip()
        }
      },
      /**
       * UiFilterModal(10051) 등 오버레이 위에서 fixed 툴팁이 가려지지 않도록 z-index 보정
       */
      resolveFixedZIndex() {
        if (this.zIndex != null) {
          return this.zIndex
        }

        const triggerEl = this.getTriggerEl()
        if (!triggerEl) return 10050

        const filterPanel = triggerEl.closest('.filter-dropdown')
        if (filterPanel) {
          const z = parseInt(window.getComputedStyle(filterPanel).zIndex, 10)
          return (Number.isFinite(z) ? z : 10051) + 10
        }

        const popup = triggerEl.closest('.ui-popup')
        if (popup) {
          let z = parseInt(window.getComputedStyle(popup).zIndex, 10)
          if (!Number.isFinite(z)) {
            const content = popup.querySelector('.ui-popup__content')
            if (content) {
              z = parseInt(window.getComputedStyle(content).zIndex, 10)
            }
          }
          const base = Number.isFinite(z) ? z : 1000
          return base + 10
        }

        const slidePanel = triggerEl.closest('.detail-panel')
        if (slidePanel) {
          const z = parseInt(window.getComputedStyle(slidePanel).zIndex, 10)
          const base = Number.isFinite(z) ? z : 1000
          return base + 10
        }

        return 10050
      },
      /**
       * fixed 모드일 때 위치 계산
       */
      calculateFixedPosition() {
        const triggerEl = this.getTriggerEl()
        if (!triggerEl) return

        const rect = triggerEl.getBoundingClientRect()
        const gap = 10

        // autoFlip: 뷰포트 상/하 여백이 부족하면 반대편으로 전환
        let nextPosition = this.position
        const bottomPositions = ['bottom', 'bottom-left', 'bottom-right']
        const topPositions = ['top', 'top-left', 'top-right']
        if (this.autoFlip && (topPositions.includes(this.position) || bottomPositions.includes(this.position))) {
          const tooltipEl = this.getTooltipEl()
          // visibility:hidden 상태에서도 사이즈 계산 가능
          const tooltipHeight = tooltipEl ? tooltipEl.offsetHeight : 0
          const margin = 8
          const viewportHeight = window.innerHeight

          if (topPositions.includes(this.position) && rect.top < tooltipHeight + gap + margin) {
            if (this.position === 'top-left') nextPosition = 'bottom-left'
            else if (this.position === 'top-right') nextPosition = 'bottom-right'
            else nextPosition = 'bottom'
          } else if (
            bottomPositions.includes(this.position) &&
            viewportHeight - rect.bottom < tooltipHeight + gap + margin
          ) {
            if (this.position === 'bottom-left') nextPosition = 'top-left'
            else if (this.position === 'bottom-right') nextPosition = 'top-right'
            else nextPosition = 'top'
          }
        }
        // 화살표/CSS 클래스 동기화를 위해 effectivePosition 반영
        this.effectivePosition = nextPosition

        switch (nextPosition) {
          case 'top':
            this.fixedStyle = {
              left: `${rect.left + rect.width / 2}px`,
              top: `${rect.top - gap}px`,
              transform: 'translate(-50%, -100%)'
            }
            break
          case 'bottom':
            this.fixedStyle = {
              left: `${rect.left + rect.width / 2}px`,
              top: `${rect.bottom + gap}px`,
              transform: 'translateX(-50%)'
            }
            break
          case 'bottom-left':
            this.fixedStyle = {
              left: `${rect.left}px`,
              top: `${rect.bottom + gap}px`,
              transform: 'none'
            }
            break
          case 'bottom-right':
            this.fixedStyle = {
              left: `${rect.right}px`,
              top: `${rect.bottom + gap}px`,
              transform: 'translateX(-100%)'
            }
            break
          case 'top-left':
            this.fixedStyle = {
              left: `${rect.left}px`,
              top: `${rect.top - gap}px`,
              transform: 'translateY(-100%)'
            }
            break
          case 'top-right':
            this.fixedStyle = {
              left: `${rect.right}px`,
              top: `${rect.top - gap}px`,
              transform: 'translate(-100%, -100%)'
            }
            break
          case 'left':
            this.fixedStyle = {
              left: `${rect.left - gap}px`,
              top: `${rect.top + rect.height / 2}px`,
              transform: 'translate(-100%, -50%)'
            }
            break
          case 'right':
            this.fixedStyle = {
              left: `${rect.right + gap}px`,
              top: `${rect.top + rect.height / 2}px`,
              transform: 'translateY(-50%)'
            }
            break
          case 'right-start':
            this.fixedStyle = {
              left: `${rect.right + gap}px`,
              top: `${rect.top}px`,
              transform: 'none'
            }
            break
        }

        this.fixedStyle = { ...this.fixedStyle, zIndex: this.resolveFixedZIndex() }
      }
    }
  }
</script>

<style lang="scss">
  @use 'sass:color';

  .ui-tooltip-wrapper {
    position: relative;
    display: inline-block;
  }

  .ui-tooltip {
    position: absolute;
    z-index: 1000;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    padding: 0;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity 0.3s,
      visibility 0.3s;
    width: max-content;
    max-width: 300px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;

    // 툴팁 화살표 공통 스타일
    &::before {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border-style: solid;
    }

    // 툴팁 위치별 스타일
    &--top {
      bottom: calc(100% + 10px);
      left: 50%;
      transform: translateX(-50%);

      &::before {
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 8px 8px 0 8px;
        border-color: #fff transparent transparent transparent;
      }
    }

    &--right {
      left: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);

      &::before {
        right: 100%;
        top: 50%;
        transform: translateY(-50%);
        border-width: 8px 8px 8px 0;
        border-color: transparent #fff transparent transparent;
      }
    }

    &--right-start {
      left: calc(100% + 10px);
      top: 0;
      transform: none;

      &::before {
        right: 100%;
        top: 12px;
        transform: none;
        border-width: 8px 8px 8px 0;
        border-color: transparent #fff transparent transparent;
      }
    }

    &--bottom {
      top: calc(100% + 10px);
      left: 50%;
      transform: translateX(-50%);
      width: max-content;
      &::before {
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 0 8px 8px 8px;
        border-color: transparent transparent #fff transparent;
      }
    }

    &--bottom-left {
      top: calc(100% + 10px);
      left: 0;
      transform: none;
      width: max-content;
      &::before {
        bottom: 100%;
        left: 12px;
        transform: none;
        border-width: 0 8px 8px 8px;
        border-color: transparent transparent #fff transparent;
      }
    }

    &--bottom-right {
      top: calc(100% + 10px);
      right: 0;
      left: auto;
      transform: none;
      width: max-content;
      &::before {
        bottom: 100%;
        right: 12px;
        left: auto;
        transform: none;
        border-width: 0 8px 8px 8px;
        border-color: transparent transparent #fff transparent;
      }
    }

    &--top-left {
      bottom: calc(100% + 10px);
      left: 0;
      transform: none;
      width: max-content;
      &::before {
        top: 100%;
        left: 12px;
        transform: none;
        border-width: 8px 8px 0 8px;
        border-color: #fff transparent transparent transparent;
      }
    }

    &--top-right {
      bottom: calc(100% + 10px);
      right: 0;
      left: auto;
      transform: none;
      width: max-content;
      &::before {
        top: 100%;
        right: 12px;
        left: auto;
        transform: none;
        border-width: 8px 8px 0 8px;
        border-color: #fff transparent transparent transparent;
      }
    }

    &--left {
      right: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);

      &::before {
        left: 100%;
        top: 50%;
        transform: translateY(-50%);
        border-width: 8px 0 8px 8px;
        border-color: transparent transparent transparent #fff;
      }
    }

    // 툴팁 표시 상태
    &--show {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }

    // fixed 모드 (overflow 문제 해결용, body Teleport)
    &--fixed {
      position: fixed;
      z-index: 10050;
      // 기존 위치 스타일 초기화
      top: auto;
      bottom: auto;
      left: auto;
      right: auto;
      transform: none;
    }

    // 툴팁 내용
    &-content {
      padding: 12px 16px;
      line-height: 1.5;
      font-size: 13px;
      color: #666;
      display: block;
      white-space: normal;
      overflow-wrap: break-word;
      word-break: keep-all;

    &--scrollable-title {
      max-height: calc(var(--tooltip-title-max-lines, 10) * 1lh);
      overflow-x: hidden;
      overflow-y: auto;
      line-height: 1.5;

      strong {
        margin-bottom: 0;
      }
    }

    strong {
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }

      p {
        margin: 4px 0;
        font-size: 13px;
        line-height: 1.5;
        color: #666;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

</style>
